import { motion } from 'framer-motion';
import { Loader2, Search, X } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import './Nav.css';

export type AppTab = 'discover' | 'profile';

type Props = {
  activeTab: AppTab;
  onTabChange: (tab: AppTab) => void;
  onOpenDrafts?: () => void;
  searchQuery: string;
  onSearchChange: (q: string) => void;
  searching?: boolean;
};

function userInitial(email: string): string {
  return email.charAt(0).toUpperCase() || '?';
}

export default function Nav({
  activeTab,
  onTabChange,
  onOpenDrafts,
  searchQuery,
  onSearchChange,
  searching,
}: Props) {
  const { user } = useAuth();

  return (
    <header className="bar">
      <div className="bar__inner app-col">
        {/* Brand — always visible, click to go back to discover */}
        <button
          className="bar__brand"
          aria-label="Back to discover"
          onClick={() => onTabChange('discover')}
        >
          <span className="bar__logo" aria-hidden>✳</span>
          <span className="bar__mark">ctf</span>
          <motion.span
            className="bar__dot bar__dot--on"
            animate={{ opacity: [1, 0.35, 1] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          />
        </button>

        {/* Profile avatar */}
        {user && (
          <button
            className="bar__avatar"
            onClick={() => {
              if (activeTab !== 'profile') {
                onTabChange('profile');
              }
              onOpenDrafts?.();
            }}
            aria-label="Your profile"
          >
            <span className="bar__avatar-letter">{userInitial(user.email ?? '')}</span>
            <motion.span
              className="bar__avatar-dot bar__avatar-dot--on"
              animate={{ opacity: [1, 0.35, 1] }}
              transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
            />
          </button>
        )}

        {/* Search bar */}
        <div className="bar__search">
          {searching ? (
            <Loader2 size={14} className="bar__search-icon bar__spin" />
          ) : (
            <Search size={14} className="bar__search-icon" />
          )}
          <input
            className="bar__search-input"
            placeholder="search the community..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
          />
          {searchQuery && (
            <button
              className="bar__search-clear"
              onClick={() => onSearchChange('')}
              aria-label="Clear search"
            >
              <X size={10} />
            </button>
          )}
        </div>
      </div>
    </header>
  );
}
